import React, { FC, ReactNode, createContext, useContext, useState, useRef, useEffect } from 'react';
import ASRApi from '../../ASRApi';
import { CATEGORIES } from '../../report-concern-modal/constants';
import { ASRIssue } from '../../types';
import { useRouter, ROUTES, ERRORS } from '../router';

const noop = () => {};

export const CreateAsrContext = createContext({
  asrIssue: {} as Partial<ASRIssue>,
  updateAsrIssue: noop as (asrIssue: Partial<ASRIssue>, options?: any) => void,
  saveAsr: noop as () => void,
});

export const useCreateAsr = () => {
  const context = useContext(CreateAsrContext);
  if (context === undefined) {
    throw new Error('useCreateAsr must be used within a Provider');
  }

  return context;
};

type CreateAsrProviderProps = {
  children: ReactNode | ReactNode[];
};

export const CreateAsrProvider: FC<CreateAsrProviderProps> = ({ children }: any) => {
  const [asrIssue, setAsrIssue] = useState<Partial<ASRIssue>>({});
  const { setIsLoading, setLoadingText, goToRoute, setError } = useRouter();
  const shouldSave = useRef<boolean>(false);

  const updateAsrIssue = (partialAsrIssue: Partial<ASRIssue>, options: any = {}) => {
    const { shouldSave: shouldSaveOption } = options;

    if (shouldSaveOption) {
      shouldSave.current = true;
    }
    setAsrIssue({ ...asrIssue, ...partialAsrIssue });
  };

  useEffect(() => {
    if(shouldSave.current) {
      shouldSave.current = false;
      saveAsr();
    }
  }, [asrIssue]);

  const saveAsr = async () => {
    setLoadingText('Reporting Concern...');
    setIsLoading(true);
    const isCosmetic = asrIssue.type === CATEGORIES.COSMETIC;
    const response = await ASRApi.createASR(asrIssue as ASRIssue);

    setIsLoading(false);
    if (response?.status === 'Succeeded') {
      goToRoute(isCosmetic ? ROUTES.COSMETIC_SUCCESS : ROUTES.MECHANIC_SUCCESS);
    } else {
      setError(ERRORS.DEFAULT);
      goToRoute(isCosmetic ? ROUTES.COSMETIC_IMAGES : ROUTES.ASR_FORM);
    }
  };

  const value = {
    asrIssue,
    updateAsrIssue,
    saveAsr,
  };

  return <CreateAsrContext.Provider value={value}>{children}</CreateAsrContext.Provider>;
};
